import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TileService } from '../../services/tile.service';
import { Tile, TileWidget, TileWidgetType } from '../../types/tile.types';
import { defaultWidgetName, serverMessage, timeZoneOptions } from './home-widget.util';

type Choice = TileWidgetType | 'link';

const CHOICES: { type: Choice; icon: string; label: string; description: string }[] = [
  { type: 'link', icon: 'ri-bookmark-line', label: 'Eigener Link', description: 'Eine Webseite als Kachel merken' },
  { type: 'note', icon: 'ri-sticky-note-line', label: 'Notiz', description: 'Text direkt in der Kachel notieren' },
  { type: 'clock', icon: 'ri-time-line', label: 'Uhr', description: 'Digital oder analog, auch mit Datum' },
  { type: 'weather', icon: 'ri-sun-cloudy-line', label: 'Wetter', description: 'Aktuelles Wetter für eine Postleitzahl' },
];

/** Overlay hinter „Hinzufügen“ in „Startseite anpassen“: Auswahl der Kachelart und Einrichtung von
 * Notiz, Uhr und Wetter. Eigene Links übernimmt weiter der Weblink-Editor (`chooseLink`). */
@Component({
  selector: 'app-home-widget-dialog',
  standalone: true,
  imports: [FormsModule, DialogModule, ButtonModule, SelectModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './home-widget-dialog.component.html',
  styleUrls: ['./home-widget-dialog.component.scss'],
})
export class HomeWidgetDialogComponent {
  /** Nach dem Speichern oder Löschen. `added` ist bei neuen Kacheln `true`. */
  @Output() public saved = new EventEmitter<{ added: boolean }>();
  @Output() public chooseLink = new EventEmitter<void>();

  public readonly choices = CHOICES;
  public readonly timeZones = timeZoneOptions();
  public visible = false;
  /** Leer: Auswahl der Kachelart. */
  public type?: TileWidgetType;
  public tileId = '';
  public name = '';
  public noteText = '';
  public clockStyle: 'digital' | 'analog' = 'digital';
  public showDate = true;
  public showSeconds = false;
  public timeZone = '';
  public postalCode = '';
  public saving = false;
  public confirmDelete = false;
  public error = '';
  private readonly tiles = inject(TileService);
  private readonly cdr = inject(ChangeDetectorRef);

  public get validPostalCode(): boolean {
    return /^\d{5}$/.test(this.postalCode.trim());
  }

  public get title(): string {
    if (!this.type) return 'Zur Startseite hinzufügen';
    const label = CHOICES.find(choice => choice.type === this.type)!.label;
    return this.tileId ? `${label} bearbeiten` : `${label} hinzufügen`;
  }

  public get namePlaceholder(): string {
    if (this.type === 'weather') return 'z. B. Büro (sonst „Wetter“ und Ort)';
    if (this.type === 'clock') return this.timeZone ? 'sonst „Uhr“ und Ort' : 'Uhr';
    return 'Notiz';
  }

  /** Ohne Kachel: Auswahl der Kachelart. Mit einer eingebauten Kachel: deren Einstellungen. */
  public open(tile?: Tile): void {
    this.reset();
    const widget = tile?.widget;
    if (tile && widget) {
      this.tileId = tile._id;
      this.type = widget.type;
      // Den Standardnamen nicht ins Feld übernehmen, damit er nach einem Ortswechsel neu entsteht.
      this.name = tile.name === defaultWidgetName(widget) ? '' : tile.name;
      if (widget.type === 'note') this.noteText = widget.text;
      if (widget.type === 'clock') {
        this.clockStyle = widget.style;
        this.showDate = widget.showDate;
        this.showSeconds = widget.showSeconds;
        this.timeZone = widget.timeZone;
      }
      if (widget.type === 'weather') this.postalCode = widget.postalCode;
    }
    this.visible = true;
    this.cdr.markForCheck();
  }

  public choose(type: Choice): void {
    if (type === 'link') {
      this.close();
      this.chooseLink.emit();
      return;
    }
    this.type = type;
    this.error = '';
  }

  public back(): void {
    this.reset();
  }

  public close(): void {
    if (this.saving) return;
    this.visible = false;
    this.cdr.markForCheck();
  }

  public async save(): Promise<void> {
    if (!this.type || this.saving || (this.type === 'weather' && !this.validPostalCode)) return;
    this.saving = true;
    this.error = '';
    try {
      const res = await this.tiles.savePersonalWidget(this.tileId, this.name.trim(), this.widget());
      if (res.status !== 'ok') throw new Error(res.message);
      const added = !this.tileId;
      this.saving = false;
      this.close();
      this.saved.emit({ added });
    } catch (error) {
      this.error = serverMessage(error, 'Die Kachel konnte nicht gespeichert werden. Bitte erneut versuchen.');
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  public async delete(): Promise<void> {
    if (!this.tileId || this.saving) return;
    if (!this.confirmDelete) {
      this.confirmDelete = true;
      return;
    }
    this.saving = true;
    this.error = '';
    try {
      const res = await this.tiles.deleteWebTile(this.tileId, 'personal');
      if (res.status !== 'ok') throw new Error(res.message);
      this.saving = false;
      this.close();
      this.saved.emit({ added: false });
    } catch (error) {
      this.error = serverMessage(error, 'Die Kachel konnte nicht gelöscht werden.');
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  private widget(): TileWidget {
    switch (this.type) {
      case 'note': return { type: 'note', text: this.noteText };
      case 'clock': return { type: 'clock', style: this.clockStyle, showDate: this.showDate, showSeconds: this.showSeconds, timeZone: this.timeZone };
      default: return { type: 'weather', postalCode: this.postalCode.trim(), place: '' };
    }
  }

  private reset(): void {
    this.type = undefined;
    this.tileId = '';
    this.name = '';
    this.noteText = '';
    this.clockStyle = 'digital';
    this.showDate = true;
    this.showSeconds = false;
    this.timeZone = '';
    this.postalCode = '';
    this.confirmDelete = false;
    this.error = '';
  }
}
